"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useId, useState, type FormEvent } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "./PasswordInput"
import { LegalAgreementText } from "./LegalAgreementText"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import { recordPendingConsent } from "@/lib/consent"
import { trackFunnelEvent } from "@/lib/analytics"
import type { AuthMode } from "./authMode"
import { SocialSignInButtons } from "./SocialSignInButtons"

interface SignUpFormProps {
  onModeChange: (mode: AuthMode, email?: string) => void
}

export function SignUpForm({ onModeChange }: SignUpFormProps) {
  const { t } = useI18n()
  const { signUp, errorKey, clearError } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [consent, setConsent] = useState(false)
  const [localErrorKey, setLocalErrorKey] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const emailId = useId()
  const passwordId = useId()
  const confirmId = useId()
  const consentId = useId()

  const activeErrorKey = localErrorKey ?? errorKey

  // G04: the sign-up form is shown inside a modal/dialog the visitor opens
  // deliberately (openAuth("signUp")) — mounting it IS the "started" signal,
  // no need to wait for a keystroke.
  useEffect(() => {
    trackFunnelEvent("signup_started")
  }, [])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLocalErrorKey(null)
    clearError()

    if (!consent) {
      setLocalErrorKey("auth.errors.consentRequired")
      return
    }
    if (password !== confirmPassword) {
      setLocalErrorKey("auth.errors.passwordMismatch")
      return
    }

    setSubmitting(true)
    try {
      const result = await signUp(email, password)
      recordPendingConsent(email)
      trackFunnelEvent("signup_completed", { method: "email" })
      if (result.userConfirmed) {
        onModeChange("signIn", email)
      } else {
        onModeChange("verify", email)
      }
    } catch {
      // errorKey is already set by the auth context.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      {/* ADR-032 (re-pointed at the 2-week free claim by item 8, 2026-09-12):
          the signup perk pill. Messaging + intent only — an unauthenticated
          checkout can't be bound to an account, so the actual claim happens
          on /app/access after onboarding, where the free-claim card is the
          default hero (prelaunch retired from new offers). */}
      <p className="self-start rounded-bb-full border border-green/40 bg-green/5 px-3 py-1.5 text-xs font-semibold text-green">
        {t("auth.freeClaimPerk")}
      </p>

      {/* Config-flagged social sign-in — renders nothing until enabled.
          First federated login auto-provisions the users row at /v1/me
          bootstrap, so social "sign up" and "log in" are the same action. */}
      <SocialSignInButtons disabled={submitting} />

      <div className="flex flex-col gap-1.5">
        <label htmlFor={emailId} className="text-sm font-medium text-ink">
          {t("auth.emailLabel")}
        </label>
        <Input
          id={emailId}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={event => setEmail(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={passwordId} className="text-sm font-medium text-ink">
          {t("auth.passwordLabel")}
        </label>
        <PasswordInput
          id={passwordId}
          name="new-password"
          autoComplete="new-password"
          minLength={8}
          required
          value={password}
          onChange={event => setPassword(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">{t("auth.passwordHint")}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={confirmId} className="text-sm font-medium text-ink">
          {t("auth.confirmPasswordLabel")}
        </label>
        <PasswordInput
          id={confirmId}
          name="confirm-password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={event => setConfirmPassword(event.target.value)}
        />
      </div>

      {/* Not a wrapping <label>: the sentence contains real links, and a
          click on a link inside a label would also toggle the checkbox. The
          checkbox is labelled via aria-labelledby instead. */}
      <div className="flex items-start gap-2 text-sm text-ink-2">
        <input
          id={consentId}
          type="checkbox"
          className="mt-0.5 size-4 shrink-0"
          checked={consent}
          onChange={event => setConsent(event.target.checked)}
          aria-labelledby={`${consentId}-text`}
          required
        />
        <span id={`${consentId}-text`}>
          <LegalAgreementText prefixKey="auth.agreeSignUpPrefix" />
        </span>
      </div>

      <p className="rounded-bb-md bg-cream px-3 py-2 text-xs text-ink-2">
        {t("auth.aiTransparency")}
      </p>

      {activeErrorKey && (
        <p role="alert" className="text-sm text-destructive">
          {t(activeErrorKey)}
        </p>
      )}

      <Button type="submit" disabled={submitting} className="min-h-11">
        {t("auth.createAccount")}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        {t("auth.alreadyHaveAccount")}{" "}
        <button
          type="button"
          className="font-medium text-green underline underline-offset-2"
          onClick={() => onModeChange("signIn")}
        >
          {t("auth.logInTitle")}
        </button>
      </p>
    </form>
  )
}
